// constants/contactLinkCategories.js - Enhanced Contact Link Categories with Backward Compatibility

// Enhanced validation functions
const validateCategoryName = (categoryName) => {
    if (!categoryName || typeof categoryName !== 'string' || categoryName.trim() === '') {
        throw new Error('Category name is required and must be a valid string');
    }

    const validCategories = ['social', 'products', 'business', 'personal', 'professional'];
    if (!validCategories.includes(categoryName.toLowerCase())) {
        throw new Error(`Invalid category name. Valid categories: ${validCategories.join(', ')}`);
    }

    return categoryName.toLowerCase();
};

const validateLinkType = (linkType) => {
    if (!linkType || typeof linkType !== 'string' || linkType.trim() === '') {
        throw new Error('Link type is required and must be a valid string');
    }

    // Basic format validation (alphanumeric, hyphens, underscores)
    const linkTypeRegex = /^[a-zA-Z0-9-_]+$/;
    if (!linkTypeRegex.test(linkType.trim())) {
        throw new Error('Link type must contain only letters, numbers, hyphens, and underscores');
    }

    return linkType.trim().toLowerCase();
};

const validateLinkConfig = (linkConfig) => {
    if (!linkConfig || typeof linkConfig !== 'object') {
        throw new Error('Link config must be a valid object');
    }

    const validConfig = {};

    if (linkConfig.name !== undefined) {
        if (typeof linkConfig.name !== 'string' || linkConfig.name.trim() === '') {
            throw new Error('Link name must be a non-empty string');
        }
        validConfig.name = linkConfig.name.trim();
    }

    if (linkConfig.icon !== undefined) {
        if (typeof linkConfig.icon !== 'string') {
            throw new Error('Link icon must be a string');
        }
        validConfig.icon = linkConfig.icon.trim();
    }

    if (linkConfig.color !== undefined) {
        if (typeof linkConfig.color !== 'string') {
            throw new Error('Link color must be a string');
        }
        // Basic hex color validation
        const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        if (!hexRegex.test(linkConfig.color.trim())) {
            throw new Error('Link color must be a valid hex color (e.g., #000000)');
        }
        validConfig.color = linkConfig.color.trim();
    }

    if (linkConfig.description !== undefined) {
        if (typeof linkConfig.description !== 'string') {
            throw new Error('Link description must be a string');
        }
        validConfig.description = linkConfig.description.trim();
    }

    if (linkConfig.enabled !== undefined) {
        validConfig.enabled = Boolean(linkConfig.enabled);
    }

    return validConfig;
};

// Enhanced error handler
const handleContactLinkError = (error, operation = 'Contact link operation') => {
    console.error(`[ContactLinkCategories] ${operation} Error:`, {
        message: error.message,
        stack: error.stack,
        operation
    });

    throw new Error(`Contact link category error: ${error.message}`);
};

// Enhanced contact link categories configuration with detailed metadata
export const CONTACT_LINK_CATEGORIES_ENHANCED = {
    social: [
        {
            name: 'linkedin',
            icon: 'linkedin-icon.svg',
            color: '#0077B5',
            description: 'Professional networking platform',
            enabled: true,
            urlPattern: 'https://linkedin.com/in/{username}',
            validation: {
                required: true,
                maxLength: 100,
                pattern: /^[a-zA-Z0-9-]+$/
            }
        },
        {
            name: 'twitter',
            icon: 'twitter-icon.svg',
            color: '#1DA1F2',
            description: 'Social media platform',
            enabled: true,
            urlPattern: 'https://twitter.com/{username}',
            validation: {
                required: true,
                maxLength: 15,
                pattern: /^[a-zA-Z0-9_]+$/
            }
        },
        {
            name: 'facebook',
            icon: 'facebook-icon.svg',
            color: '#1877F2',
            description: 'Social networking platform',
            enabled: true,
            urlPattern: 'https://facebook.com/{username}',
            validation: {
                required: true,
                maxLength: 50,
                pattern: /^[a-zA-Z0-9.]+$/
            }
        },
        {
            name: 'instagram',
            icon: 'instagram-icon.svg',
            color: '#E4405F',
            description: 'Photo and video sharing platform',
            enabled: true,
            urlPattern: 'https://instagram.com/{username}',
            validation: {
                required: true,
                maxLength: 30,
                pattern: /^[a-zA-Z0-9._]+$/
            }
        },
        {
            name: 'youtube',
            icon: 'youtube-icon.svg',
            color: '#FF0000',
            description: 'Video sharing platform',
            enabled: true,
            urlPattern: 'https://youtube.com/@{username}',
            validation: {
                required: true,
                maxLength: 30,
                pattern: /^[a-zA-Z0-9_-]+$/
            }
        },
        {
            name: 'tiktok',
            icon: 'tiktok-icon.svg',
            color: '#000000',
            description: 'Short-form video platform',
            enabled: true,
            urlPattern: 'https://tiktok.com/@{username}',
            validation: {
                required: true,
                maxLength: 24,
                pattern: /^[a-zA-Z0-9._]+$/
            }
        },
        {
            name: 'github',
            icon: 'github-icon.svg',
            color: '#181717',
            description: 'Code hosting and collaboration platform',
            enabled: true,
            urlPattern: 'https://github.com/{username}',
            validation: {
                required: true,
                maxLength: 39,
                pattern: /^[a-zA-Z0-9-]+$/
            }
        },
        {
            name: 'whatsapp',
            icon: 'whatsapp-icon.svg',
            color: '#25D366',
            description: 'Messaging platform',
            enabled: true,
            urlPattern: 'https://wa.me/{phone}',
            validation: {
                required: true,
                maxLength: 15,
                pattern: /^[0-9+]+$/
            }
        },
        {
            name: 'telegram',
            icon: 'telegram-icon.svg',
            color: '#0088CC',
            description: 'Messaging platform',
            enabled: true,
            urlPattern: 'https://t.me/{username}',
            validation: {
                required: true,
                maxLength: 32,
                pattern: /^[a-zA-Z0-9_]+$/
            }
        },
        {
            name: 'snapchat',
            icon: 'snapchat-icon.svg',
            color: '#FFFC00',
            description: 'Photo and video sharing platform',
            enabled: true,
            urlPattern: 'https://www.snapchat.com/add/{username}',
            validation: {
                required: true,
                maxLength: 15,
                pattern: /^[a-zA-Z0-9._]+$/
            }
        },
        {
            name: 'pinterest',
            icon: 'pinterest-icon.svg',
            color: '#E60023',
            description: 'Visual discovery platform',
            enabled: true,
            urlPattern: 'https://pinterest.com/{username}',
            validation: {
                required: true,
                maxLength: 30,
                pattern: /^[a-zA-Z0-9._]+$/
            }
        },
        {
            name: 'discord',
            icon: 'discord-icon.svg',
            color: '#5865F2',
            description: 'Gaming and community platform',
            enabled: true,
            urlPattern: 'https://discord.com/users/{username}',
            validation: {
                required: true,
                maxLength: 32,
                pattern: /^[a-zA-Z0-9_]+$/
            }
        },
        {
            name: 'wechat',
            icon: 'wechat-icon.svg',
            color: '#07C160',
            description: 'Messaging and social platform',
            enabled: true,
            urlPattern: 'weixin://dl/chat?{username}',
            validation: {
                required: true,
                maxLength: 20,
                pattern: /^[a-zA-Z0-9_-]+$/
            }
        },
        {
            name: 'threads',
            icon: 'threads-icon.svg',
            color: '#000000',
            description: 'Text-based social platform',
            enabled: true,
            urlPattern: 'https://www.threads.net/@{username}',
            validation: {
                required: true,
                maxLength: 30,
                pattern: /^[a-zA-Z0-9._]+$/
            }
        }
    ],

    products: [
        {
            name: 'product',
            icon: 'product-icon.svg',
            color: '#4CAF50',
            description: 'Product showcase or sales page',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'service',
            icon: 'service-icon.svg',
            color: '#2196F3',
            description: 'Service offering or consultation',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'products & services',
            icon: 'products-services-icon.svg',
            color: '#FF9800',
            description: 'Products and services showcase',
            enabled: true,
            urlPattern: 'custom',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^.*$/
            }
        },
        {
            name: 'booking',
            icon: 'booking-icon.svg',
            color: '#FF9800',
            description: 'Appointment or booking system',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'calendar',
            icon: 'calendar-icon.svg',
            color: '#9C27B0',
            description: 'Calendar scheduling link',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'shop',
            icon: 'shop-icon.svg',
            color: '#E91E63',
            description: 'Online store or marketplace',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'course',
            icon: 'course-icon.svg',
            color: '#607D8B',
            description: 'Educational course or training',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'event',
            icon: 'event-icon.svg',
            color: '#795548',
            description: 'Event registration or information',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'ticket',
            icon: 'ticket-icon.svg',
            color: '#FF5722',
            description: 'Ticket sales or reservations',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'donation',
            icon: 'donation-icon.svg',
            color: '#4CAF50',
            description: 'Donation or fundraising link',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'subscription',
            icon: 'subscription-icon.svg',
            color: '#673AB7',
            description: 'Subscription service link',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        }
    ],

    business: [
        {
            name: 'website',
            icon: 'website-icon.svg',
            color: '#3F51B5',
            description: 'Main business website',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'portfolio',
            icon: 'portfolio-icon.svg',
            color: '#009688',
            description: 'Portfolio or work showcase',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'blog',
            icon: 'blog-icon.svg',
            color: '#FF5722',
            description: 'Blog or content platform',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'newsletter',
            icon: 'newsletter-icon.svg',
            color: '#673AB7',
            description: 'Newsletter subscription',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'calendly',
            icon: 'calendly-icon.svg',
            color: '#006BFF',
            description: 'Scheduling and appointment booking',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        },
        {
            name: 'shopify',
            icon: 'shopify-icon.svg',
            color: '#95BF47',
            description: 'E-commerce store',
            enabled: true,
            urlPattern: '{url}',
            validation: {
                required: true,
                maxLength: 500,
                pattern: /^https?:\/\/.+/
            }
        }
    ],

    personal: [
        {
            name: 'email',
            icon: 'email-icon.svg',
            color: '#FFC107',
            description: 'Direct email contact',
            enabled: true,
            urlPattern: 'mailto:{email}',
            validation: {
                required: true,
                maxLength: 254,
                pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            }
        },
        {
            name: 'phone',
            icon: 'phone-icon.svg',
            color: '#4CAF50',
            description: 'Direct phone contact',
            enabled: true,
            urlPattern: 'tel:{phone}',
            validation: {
                required: true,
                maxLength: 20,
                pattern: /^[+]?[0-9\s\-\(\)]+$/
            }
        },
        {
            name: 'location',
            icon: 'location-icon.svg',
            color: '#F44336',
            description: 'Physical location or address',
            enabled: true,
            urlPattern: 'https://maps.google.com/?q={address}',
            validation: {
                required: true,
                maxLength: 200,
                pattern: /.+/
            }
        },
        {
            name: 'text',
            icon: 'text-icon.svg',
            color: '#2196F3',
            description: 'SMS text messaging',
            enabled: true,
            urlPattern: 'sms:{phone}',
            validation: {
                required: true,
                maxLength: 20,
                pattern: /^[+]?[0-9\s\-\(\)]+$/
            }
        },
        {
            name: 'facetime',
            icon: 'facetime-icon.svg',
            color: '#007AFF',
            description: 'FaceTime video calling',
            enabled: true,
            urlPattern: 'facetime:{phone}',
            validation: {
                required: true,
                maxLength: 20,
                pattern: /^[+]?[0-9\s\-\(\)]+$/
            }
        }
    ]
};

// BACKWARD COMPATIBILITY: Export the original simple array structure
// This maintains compatibility with existing code that expects simple arrays
export const CONTACT_LINK_CATEGORIES = {
    social: CONTACT_LINK_CATEGORIES_ENHANCED.social.map(item => item.name),
    products: CONTACT_LINK_CATEGORIES_ENHANCED.products.map(item => item.name),
    business: CONTACT_LINK_CATEGORIES_ENHANCED.business.map(item => item.name),
    personal: CONTACT_LINK_CATEGORIES_ENHANCED.personal.map(item => item.name)
};

// Helper functions for contact link management
export const getCategoryLinks = (categoryName) => {
    try {
        const validCategory = validateCategoryName(categoryName);
        const category = CONTACT_LINK_CATEGORIES_ENHANCED[validCategory];

        if (!category) {
            throw new Error(`Category '${validCategory}' not found`);
        }

        return category;
    } catch (error) {
        handleContactLinkError(error, `Getting category links: ${categoryName}`);
    }
};

export const getLinkConfig = (categoryName, linkName) => {
    try {
        const validCategory = validateCategoryName(categoryName);
        const validLinkName = validateLinkType(linkName);

        const category = CONTACT_LINK_CATEGORIES_ENHANCED[validCategory];
        if (!category) {
            throw new Error(`Category '${validCategory}' not found`);
        }

        const linkConfig = category.find(link => link.name === validLinkName);
        if (!linkConfig) {
            throw new Error(`Link '${validLinkName}' not found in category '${validCategory}'`);
        }

        return linkConfig;
    } catch (error) {
        handleContactLinkError(error, `Getting link config: ${categoryName}/${linkName}`);
    }
};

export const getAllCategories = () => {
    try {
        return Object.keys(CONTACT_LINK_CATEGORIES_ENHANCED).map(category => ({
            name: category,
            displayName: category.charAt(0).toUpperCase() + category.slice(1),
            linkCount: CONTACT_LINK_CATEGORIES_ENHANCED[category].length,
            enabledLinks: CONTACT_LINK_CATEGORIES_ENHANCED[category].filter(link => link.enabled).length
        }));
    } catch (error) {
        handleContactLinkError(error, 'Getting all categories');
    }
};

export const getEnabledLinks = (categoryName = null) => {
    try {
        if (categoryName) {
            const category = getCategoryLinks(categoryName);
            return category.filter(link => link.enabled);
        }

        const enabledLinks = [];
        for (const [category, links] of Object.entries(CONTACT_LINK_CATEGORIES_ENHANCED)) {
            const enabled = links.filter(link => link.enabled);
            enabledLinks.push(...enabled.map(link => ({ ...link, category })));
        }

        return enabledLinks;
    } catch (error) {
        handleContactLinkError(error, 'Getting enabled links');
    }
};

export const validateLinkData = (categoryName, linkName, linkData) => {
    try {
        const linkConfig = getLinkConfig(categoryName, linkName);
        const validation = linkConfig.validation;

        if (validation.required && (!linkData || linkData.trim() === '')) {
            throw new Error(`${linkConfig.name} is required`);
        }

        if (linkData && validation.maxLength && linkData.length > validation.maxLength) {
            throw new Error(`${linkConfig.name} must be less than ${validation.maxLength} characters`);
        }

        if (linkData && validation.pattern && !validation.pattern.test(linkData)) {
            throw new Error(`${linkConfig.name} format is invalid`);
        }

        return true;
    } catch (error) {
        handleContactLinkError(error, `Validating link data: ${categoryName}/${linkName}`);
    }
};

export const generateLinkUrl = (categoryName, linkName, linkData) => {
    try {
        const linkConfig = getLinkConfig(categoryName, linkName);

        if (!linkData) {
            throw new Error('Link data is required to generate URL');
        }

        // Replace placeholders in URL pattern
        let url = linkConfig.urlPattern;

        if (linkName === 'email') {
            url = url.replace('{email}', encodeURIComponent(linkData));
        } else if (linkName === 'phone') {
            url = url.replace('{phone}', encodeURIComponent(linkData));
        } else if (linkName === 'location') {
            url = url.replace('{address}', encodeURIComponent(linkData));
        } else if (linkConfig.urlPattern.includes('{username}')) {
            url = url.replace('{username}', encodeURIComponent(linkData));
        } else {
            url = linkData; // For custom URLs
        }

        return url;
    } catch (error) {
        handleContactLinkError(error, `Generating link URL: ${categoryName}/${linkName}`);
    }
};

export const searchLinks = (searchTerm) => {
    try {
        if (!searchTerm || typeof searchTerm !== 'string') {
            throw new Error('Search term must be a valid string');
        }

        const searchResults = [];
        const term = searchTerm.toLowerCase();

        for (const [category, links] of Object.entries(CONTACT_LINK_CATEGORIES_ENHANCED)) {
            const matches = links.filter(link =>
                link.name.toLowerCase().includes(term) ||
                link.description.toLowerCase().includes(term)
            );

            if (matches.length > 0) {
                searchResults.push(...matches.map(link => ({ ...link, category })));
            }
        }

        return searchResults;
    } catch (error) {
        handleContactLinkError(error, 'Searching links');
    }
};

// Enhanced function to get link metadata by name (useful for components)
export const getLinkMetadata = (linkName) => {
    try {
        if (!linkName || typeof linkName !== 'string') {
            throw new Error('Link name is required and must be a string');
        }

        const normalizedName = linkName.toLowerCase().trim();

        for (const [category, links] of Object.entries(CONTACT_LINK_CATEGORIES_ENHANCED)) {
            const link = links.find(l => l.name === normalizedName);
            if (link) {
                return { ...link, category };
            }
        }

        return null; // Link not found
    } catch (error) {
        handleContactLinkError(error, `Getting link metadata: ${linkName}`);
    }
};

// Function to get all link names (for backward compatibility)
export const getAllLinkNames = () => {
    try {
        const allNames = [];
        for (const [category, links] of Object.entries(CONTACT_LINK_CATEGORIES_ENHANCED)) {
            allNames.push(...links.map(link => ({ name: link.name, category })));
        }
        return allNames;
    } catch (error) {
        handleContactLinkError(error, 'Getting all link names');
    }
};

// Export the main configuration for backward compatibility
export default CONTACT_LINK_CATEGORIES; 